import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import DetailsCard from "@/components/DetailsCard";
import EmptyState from "@/components/EmptyState";
import SideSheetModal from "@/components/SideSheetModal";
import Tag, { type TagType } from "@/components/Tag/Tag";
import usePagination from "@/hooks/usePagination";
import usePageTitle from "@/hooks/usePageTitle";
import CommissionTabLayout from "../_CommissionTabLayout";
import { formatEnumLabel } from "../_utils";
import { useCanShowCommissionPlatformBool, useRequireCommissionPlatform } from "../useCommissionPlatformGate";
import { COMMISSION_PROGRAMS_QUERY } from "./queries";
import { getColumns } from "./_components/TableColumns";
import type { CommissionProgramNode } from "./types";

function programStatusTagType(status: CommissionProgramNode["status"]): TagType {
  return status === "ACTIVE" ? "success" : "surface";
}

export default function CommissionProgramsPage() {
  usePageTitle("/billing/finance/commission/programs", "Programs");
  useRequireCommissionPlatform();
  const canQuery = useCanShowCommissionPlatformBool();
  const [selectedProgram, setSelectedProgram] = useState<CommissionProgramNode | null>(null);
  const { paginationVariables, createHeaderPagination, createFooterPagination } = usePagination();
  const columns = useMemo(() => getColumns(), []);
  const { data, previousData, loading, error } = useQuery(COMMISSION_PROGRAMS_QUERY, {
    variables: paginationVariables,
    skip: !canQuery
  });

  const connection = data?.commissionPrograms ?? previousData?.commissionPrograms;
  const items = connection?.nodes ?? [];
  const totalRowCount = connection?.totalCount ?? items.length;

  if (error) {
    return (
      <CommissionTabLayout>
        <EmptyState title="Error" caption="An error occurred while loading programs." iconName="error" />
      </CommissionTabLayout>
    );
  }

  return (
    <CommissionTabLayout>
      <TableHeading body="Commission benefit programs configured for sponsor payouts." />
      <Table
        data={items}
        columns={columns}
        loading={loading}
        onClickRow={(row) => setSelectedProgram(row.original)}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Programs Yet." caption="Programs will appear here once configured." iconName="list" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(connection, {
              loading,
              pageCount: items.length,
              totalCount: totalRowCount
            })}
            onSortClick={onSortClick}
          />
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(connection, {
              loading,
              pageCount: items.length,
              totalCount: totalRowCount
            })}
            totalRowCount={totalRowCount}
          />
        )}
      />
      <SideSheetModal
        isSidebarOpen={!!selectedProgram}
        setSidebarOpen={(open) => {
          if (!open) setSelectedProgram(null);
        }}
        header={{ title: selectedProgram?.name ?? "Program details" }}
        disableFocusManagement
      >
        {selectedProgram && (
          <DetailsCard
            compact
            heading="Details"
            options={[
              {
                overline: "Name",
                headline: selectedProgram.name
              },
              {
                overline: "Slug",
                headline: selectedProgram.slug
              },
              {
                overline: "Status",
                headline: formatEnumLabel(selectedProgram.status),
                status: () => (
                  <Tag tagType={programStatusTagType(selectedProgram.status)} tagStyle="strong">
                    {formatEnumLabel(selectedProgram.status)}
                  </Tag>
                )
              }
            ]}
          />
        )}
      </SideSheetModal>
    </CommissionTabLayout>
  );
}
